import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { checkIsDriverDisabled, checkIsDriverOffline } from '../util'
import { formatters } from '@/utils/intl/dateTimeFormatters'
import { HomeFilled, MoonFilled, SunFilled } from '@ant-design/icons'
import { DriverStatusIcon } from './DriverStatusIcon'
import type { DriverSchedule } from '../types'
import { useState } from 'react'
import { DriverFlags } from './DriverFlags'
import { Card } from 'antd'
import '../styles.css'
import { startCase } from 'lodash'

type DriverCardProps = {
  driverSchedule: DriverSchedule
  handleDriverSelect: (driverId: string) => void
}

export function DriverCard({ driverSchedule, handleDriverSelect }: DriverCardProps) {
  const [isHovering, setIsHovering] = useState(false)

  const started = !!driverSchedule.last_update?.last_heartbeat

  const isDisabled = checkIsDriverDisabled(driverSchedule)
  const isOffline = checkIsDriverOffline(driverSchedule.last_update?.last_heartbeat, driverSchedule.complete)

  return (
    <Card
      styles={{
        body: {
          display: 'flex',
          flexDirection: 'column',
          padding: '1em',
        },
      }}
      style={{
        border: isDisabled ? 'none' : !isHovering ? '1px solid var(--gray-300)' : '1px solid var(--theme-color-2)',
        borderRadius: 8,
        backgroundColor: isDisabled ? 'var(--gray-100)' : !isHovering ? 'transparent' : 'var(--theme-color-2-dim)',
        marginBottom: '1em',
        cursor: isDisabled ? 'default' : 'pointer',
        pointerEvents: isDisabled ? 'none' : 'auto',
      }}
      onClick={() => handleDriverSelect(driverSchedule.driver_id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleDriverSelect(driverSchedule.driver_id)
        }
      }}
      tabIndex={0}
      role='button'
      onMouseOver={() => setIsHovering(true)}
      onMouseOut={() => setIsHovering(false)}
      onFocus={() => setIsHovering(true)}
      onBlur={() => setIsHovering(false)}
      aria-disabled={isDisabled}
    >
      <Horizontal gap={8}>
        <Texto
          category='h4'
          style={{ color: !isDisabled && isHovering ? 'var(--theme-color-2)' : 'var(--text-color)' }}
          weight={'bold'}
        >
          {driverSchedule.driver_name}
        </Texto>

        <Vertical alignItems='flex-end'>
          <Horizontal verticalCenter gap={8}>
            <DriverFlags
              hasNoGPS={driverSchedule.breadcrumbs?.length === 0 && started}
              isOffline={isOffline}
              lastHeartbeat={driverSchedule.last_update?.last_heartbeat}
              timezone={driverSchedule.timezone}
            />
            <DriverStatusIcon
              action={driverSchedule.last_update?.detail?.action}
              isCompleted={driverSchedule.last_update?.detail?.complete}
            />
          </Horizontal>

          <Texto category='label' className='mt-1'>
            {driverSchedule.complete
              ? 'Shift Complete'
              : startCase(driverSchedule.last_update?.detail?.action || 'Not Started')}
          </Texto>
        </Vertical>
      </Horizontal>
      <Horizontal>
        <Vertical justifyContent='space-between' style={{ flex: 1 }}>
          <Horizontal>
            {driverSchedule.shift === 'am' ? (
              <SunFilled style={{ color: 'var(--theme-color-1)' }} />
            ) : (
              <MoonFilled style={{ color: 'var(--theme-color-1)' }} />
            )}
            <Texto className='ml-2'>
              {formatters.TIME(driverSchedule.shift_start, driverSchedule.timezone)} -{' '}
              {formatters.TIME(driverSchedule.shift_end, driverSchedule.timezone)}
            </Texto>
          </Horizontal>
          <Texto>
            <HomeFilled className='mr-1' /> {driverSchedule.depot_name}
          </Texto>
        </Vertical>
        <Vertical justifyContent='space-between' alignItems='flex-end' style={{ flex: 1, textAlign: 'right' }}>
          <Texto>Curr. Order: {driverSchedule.last_update?.detail?.order_number || 'N/A'}</Texto>

          <Texto>
            {driverSchedule.last_update?.detail?.projected_end_time && !driverSchedule.complete
              ? `${formatters.TIME(driverSchedule.last_update?.detail?.projected_end_time)} ETA`
              : 'ETA N/A'}
          </Texto>
        </Vertical>
      </Horizontal>
    </Card>
  )
}
